import { CalendarDays, Landmark, Search, Store, type LucideIcon } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { apiPaths, type EventsResponse, type PlacesResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import { slugify } from '../../../shared/lib/slug.ts';
import { PageContainer } from '../../components/layout/PageContainer.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatDayTime } from '../../lib/format.ts';
import { PLACE_CATEGORY_LABELS } from '../places/labels.ts';

const MIN_LENGTH = 2;
const MAX_PER_GROUP = 20;

/** Texte comparable : sans accents, sans casse, ponctuation réduite (« Médiathèque » ≈ « mediatheque »). */
const normalize = (text: string) => slugify(text, 500).replace(/-/g, ' ');

interface Result {
  readonly id: string;
  readonly title: string;
  readonly meta?: string;
  readonly to?: string;
  readonly href?: string | undefined;
}

function ResultGroup({ title, icon: Icon, results }: { title: string; icon: LucideIcon; results: readonly Result[] }) {
  if (results.length === 0) return null;
  return (
    <section className="card search-group" aria-label={title}>
      <h2 className="card__title">
        <Icon size={20} aria-hidden="true" />
        {title} <span className="search-group__count">{results.length}</span>
      </h2>
      <ul className="item-list">
        {results.slice(0, MAX_PER_GROUP).map((r) => (
          <li key={r.id}>
            {r.to !== undefined ? (
              <Link to={r.to}>{r.title}</Link>
            ) : r.href !== undefined ? (
              <a href={r.href} rel="noopener noreferrer" target="_blank">
                {r.title}
              </a>
            ) : (
              <strong>{r.title}</strong>
            )}
            {r.meta !== undefined && <span className="item-list__meta">{r.meta}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Recherche locale dans les données déjà publiées (services publics, commerces OSM, agenda) : aucun service de
 * recherche tiers, aucune requête supplémentaire vers les fournisseurs.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [draft, setDraft] = useState(initial);
  // Nouvelle recherche lancée depuis l'en-tête alors que la page est ouverte : le champ suit l'URL.
  const [lastInitial, setLastInitial] = useState(initial);
  if (lastInitial !== initial) {
    setLastInitial(initial);
    setDraft(initial);
  }
  const query = normalize(initial);

  const city = DEFAULT_CITY.slug;
  const places = useApi<PlacesResponse>(apiPaths.places(city));
  const shops = useApi<PlacesResponse>(apiPaths.shops(city));
  const events = useApi<EventsResponse>(apiPaths.events(city));

  const groups = useMemo(() => {
    if (query.length < MIN_LENGTH) return null;
    const matches = (...fields: (string | undefined)[]) =>
      fields.some((f) => f !== undefined && normalize(f).includes(query));
    const placeResults = (list: PlacesResponse['places']['data']): Result[] =>
      (list ?? [])
        .filter((p) => matches(p.name, p.address, PLACE_CATEGORY_LABELS[p.category]))
        .map((p) => ({
          id: p.id,
          title: p.name,
          meta: [PLACE_CATEGORY_LABELS[p.category], p.address].filter(Boolean).join(' · '),
          to: `/lieux/${p.slug}`,
        }));
    return {
      services: placeResults(places.data?.places.data ?? null),
      shops: placeResults(shops.data?.places.data ?? null),
      events: (events.data?.events.data ?? [])
        .filter((e) => matches(e.title, e.venue?.name))
        .map((e) => ({ id: e.id, title: e.title, meta: formatDayTime(e.startsAt), href: e.url })),
    };
  }, [query, places.data, shops.data, events.data]);

  const loading = places.loading || shops.loading || events.loading;
  const total = groups === null ? 0 : groups.services.length + groups.shops.length + groups.events.length;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = draft.trim();
    setParams(q === '' ? {} : { q }, { replace: true });
  };

  return (
    <PageContainer path="/recherche">
      <form className="search-box" role="search" onSubmit={submit}>
        <label htmlFor="search-input" className="visually-hidden">
          Rechercher un lieu, un commerce, un événement
        </label>
        <Search size={20} aria-hidden="true" />
        <input
          id="search-input"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Médiathèque, boulangerie, marché…"
          autoComplete="off"
        />
        <button type="submit" className="button button--primary">
          Rechercher
        </button>
      </form>

      <div aria-live="polite">
        {groups === null ? (
          <p className="state-message">Saisissez au moins {MIN_LENGTH} caractères.</p>
        ) : loading && total === 0 ? (
          <div aria-hidden="true">
            <Skeleton lines={3} />
          </div>
        ) : total === 0 ? (
          <div className="empty-state">
            <Search size={28} aria-hidden="true" />
            <p>Aucun résultat pour « {initial} ».</p>
          </div>
        ) : (
          <>
            <p className="search-summary">
              {total} résultat{total > 1 ? 's' : ''} pour « {initial} »
            </p>
            <ResultGroup title="Services publics" icon={Landmark} results={groups.services} />
            <ResultGroup title="Commerces et lieux utiles" icon={Store} results={groups.shops} />
            <ResultGroup title="Événements" icon={CalendarDays} results={groups.events} />
          </>
        )}
      </div>
    </PageContainer>
  );
}
